/** Same-origin app path: leading slash, no protocol, no protocol-relative URL. */
export function isSafeAppPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('://');
}

const FORBIDDEN_DESTINATION_PREFIXES = [
  '/login',
  '/logout',
  '/signup',
  '/onboarding',
  '/reset-password',
  '/forgot-password',
  '/verify-email',
  '/verification-required',
];

function normalizedDestinationPathname(path: string): string {
  const pathname = (path.split('?')[0]?.split('#')[0] ?? path).toLowerCase();
  return pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
}

/** Home and Dashboard are the default post-auth landings, never a remembered intent. */
export function isDefaultDestinationPath(path: string): boolean {
  const normalized = normalizedDestinationPathname(path);
  return normalized === '/dashboard' || normalized === '' || normalized === '/';
}

/**
 * Validates that a path is safe to use as an internal application destination:
 * relative, safe characters, valid URI encoding, and prevents authentication / onboarding redirect loops.
 */
export function isSafeDestinationPath(path: string | null | undefined): path is string {
  if (typeof path !== 'string' || path.length === 0) {
    return false;
  }

  if (!isSafeAppPath(path) || path.includes('\\')) {
    return false;
  }

  const firstPrintableCharacterCode = 32;
  const deleteCharacterCode = 127;
  for (let index = 0; index < path.length; index += 1) {
    const code = path.charCodeAt(index);
    if (code < firstPrintableCharacterCode || code === deleteCharacterCode) {
      return false;
    }
  }

  // Prevent encoded traversal, protocol, backslash, or malformed percent-encoding in the path.
  // Query values may legitimately carry an encoded URL (pricing strips an unsafe `return_to` itself).
  try {
    const decoded = decodeURIComponent(path.split(/[?#]/)[0] ?? path);
    if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('\\') || decoded.includes('://')) {
      return false;
    }
  } catch {
    return false;
  }

  // Ensure standard URL parser confirms path stays on the internal origin
  try {
    const parsed = new URL(path, 'http://starthub.internal');
    if (parsed.origin !== 'http://starthub.internal' || parsed.protocol !== 'http:') {
      return false;
    }
  } catch {
    return false;
  }

  const normalizedPath = normalizedDestinationPathname(path);
  for (const prefix of FORBIDDEN_DESTINATION_PREFIXES) {
    if (normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) {
      return false;
    }
  }

  return true;
}

/** A safe destination the user explicitly asked for, excluding the default Home/Dashboard landings. */
export function isRequestedDestinationPath(path: string | null | undefined): path is string {
  return isSafeDestinationPath(path) && !isDefaultDestinationPath(path);
}
